import { useLazyQuery, useQuery } from "@apollo/client";
import { useState } from "react";
import { useParams } from "react-router-dom";
import Button from "../../components/Button/Button";
import InputField from "../../components/InputField/InputField";
import {
  getHopMiraklSsoQueryParametersQuery,
  getHopVerificationQuestionsQuery,
  validateHopRequestQuery,
  validateHopVerificationQuestionsQuery,
} from "./queries";
import "./HOP.scss";
import { HopMiraklSsoQueryParameters, ShopVerificationQuestion } from "TypesAndInterfaces";
import Modal from "components/Modal/Modal";
import useCookie from "hooks/useCookie";
import { displayError } from "utils/displayNotification";

interface ShopVerificationAnswer extends ShopVerificationQuestion {
  value: string;
}

const HOP = () => {
  let { marketplaceId, shopId }: any = useParams();
  const [inputs, setInputs] = useState<Partial<ShopVerificationAnswer>[]>([]);
  const [displayTooltip, setDisplayTooltip] = useState(false);
  const [requestedTooltip, setRequestedTooltip] = useState("");
  const [, setHopStateCookie] = useCookie("hopState");

  const {
    data: validateHopRequestData,
    loading: validateHopRequestQueryLoading,
    error: validateHopRequestQueryError,
  } = useQuery(validateHopRequestQuery, {
    variables: { marketplaceId, shopId },
    skip: !marketplaceId && !shopId,
  });

  const {
    data: getHopVerificationQuestionsData,
    loading: getHopVerificationQuestionsQueryLoading,
  } = useQuery<{ getHopVerificationQuestions: ShopVerificationQuestion[] }>(
    getHopVerificationQuestionsQuery,
    {
      variables: { marketplaceId, shopId },
      skip: validateHopRequestQueryLoading || !validateHopRequestData || !!validateHopRequestData?.validateHopRequest?.miraklSsoEnabled,
      onCompleted: (data) => {
        setInputs(data.getHopVerificationQuestions);
      },
    }
  );

  const {
    data: getHopMiraklSsoQueryParametersData,
    error: getHopMiraklSsoQueryParametersError,
    loading: getHopMiraklSsoQueryParametersQueryLoading,
  } = useQuery<{ getHopMiraklSsoQueryParameters: HopMiraklSsoQueryParameters }>(
    getHopMiraklSsoQueryParametersQuery,
    {
      variables: { marketplaceId, shopId },
      skip: validateHopRequestQueryLoading || !validateHopRequestData || !validateHopRequestData?.validateHopRequest?.miraklSsoEnabled,
    }
  );

  const responses = inputs.map((input) => {
    return {
      key: input.key,
      response: input.value || "",
    };
  });

  const [
    validateHopVerificationQuestions,
    {
      loading: validateHopVerificationQuestionsLoading,
      data: validateHopVerificationQuestionsData,
    },
  ] = useLazyQuery<{
    validateHopVerificationQuestions: {
      invalidResponseKeys: string[];
      redirectUrl: string;
    };
  }>(validateHopVerificationQuestionsQuery, {
    onError: (error) => {
      displayError(error.message)
    },
    onCompleted: (data) => {
      if (data?.validateHopVerificationQuestions?.redirectUrl) {
        window.open(
          data?.validateHopVerificationQuestions?.redirectUrl,
          "_self"
        );
      }
    },
  });

  if (
    (validateHopRequestQueryLoading ||
      getHopVerificationQuestionsQueryLoading ||
      validateHopVerificationQuestionsLoading ||
      getHopMiraklSsoQueryParametersQueryLoading) &&
    (!getHopVerificationQuestionsData || !getHopMiraklSsoQueryParametersData)
  ) {
    return <p>Loading</p>;
  }
  const renderInputs = (index: number) => {
    return (
      <div key={inputs[index].key} className="hop-input-container">
        <InputField
          label={inputs[index].title}
          value={inputs[index].value || ""}
          onChange={(e) => {
            const shallow = { ...inputs[index], value: e.target.value };
            setInputs([
              ...inputs.slice(0, index),
              shallow,
              ...inputs.slice(index + 1),
            ]);
          }}
          className={
            validateHopVerificationQuestionsData?.validateHopVerificationQuestions.invalidResponseKeys?.includes(
              inputs[index].key!
            )
              ? "invalid-input"
              : ""
          }
        />
        <Button
          type="button"
          className="button--solid"
          icon="question"
          title="Show me!"
          onClick={() => {
            setRequestedTooltip(
              `/hop-helpers/tooltip-${inputs[index].key}.png`
            );
            setDisplayTooltip(true);
          }}
        ></Button>
      </div>
    );
  };

  const handleMiraklSsoClick = (data: { getHopMiraklSsoQueryParameters: HopMiraklSsoQueryParameters; } | undefined) => {
    if (data) {
      const { url, clientId, tenant, audience, state } = data.getHopMiraklSsoQueryParameters;
      setHopStateCookie(state, 7);
      const redirectUri = `${window.location.origin}/hop/auth/mirakl?tenant=${tenant}&response_type=code&audience=${audience}&state=${state}`;
      window.location.href = `${url}/authorize?client_id=${clientId}&scope=openid&redirect_uri=${redirectUri}`;
    }
  }

  const badRequest = !validateHopRequestData?.validateHopRequest &&
    (!validateHopRequestData?.validateHopRequest?.miraklSsoEnabled ||
      (getHopMiraklSsoQueryParametersData && getHopMiraklSsoQueryParametersData.getHopMiraklSsoQueryParameters));

  return (
    <div className="page">
      <main className="main-container-wrapper hop-wrapper">
        {badRequest && (
          <div className="error-container">
            {validateHopRequestQueryError ? <p className="error-message">Error: {validateHopRequestQueryError.message}</p> : <p>Bad Request</p>}
          </div>
        )}
        {!badRequest && (
          <>
            <h2>{validateHopRequestData?.validateHopRequest?.marketplaceName}</h2>
            <p>
              In order to prove your identity, please provide us some information
              from your <strong>Mirakl</strong> account.
            </p>
            {!!validateHopRequestData?.validateHopRequest?.miraklSsoEnabled ?
              (<div>
                <button onClick={() => handleMiraklSsoClick(getHopMiraklSsoQueryParametersData)} className="hop-btn_connect" disabled={!!getHopMiraklSsoQueryParametersError}>
                  <div className="hop-logo_mirakl">
                    <img src="/mirakl-logo.svg" alt="Mirakl Logo" />
                  </div>
                  <span>Sign in with Mirakl</span>
                </button>
                {!!getHopMiraklSsoQueryParametersError && <p className="hop-error_mirakl">Technical error: <code>{getHopMiraklSsoQueryParametersError.message}</code><br /> Please contact your operator</p>}
              </div>) :
              (<>
                <form
                  className="hop-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    validateHopVerificationQuestions({
                      variables: { responses, shopId, marketplaceId },
                    });
                  }}
                >
                  {inputs.map((item, index) => {
                    return renderInputs(index);
                  })}
                  <Button
                    type="submit"
                    className="button--primary hop-button"
                    content="Submit"
                    disabled={
                      validateHopRequestQueryLoading ||
                      validateHopVerificationQuestionsLoading ||
                      getHopVerificationQuestionsQueryLoading
                    }
                  />
                </form>
                <Modal
                  title=""
                  className="hop-helper-image"
                  isOpened={displayTooltip}
                  setIsOpenedCallback={() => setDisplayTooltip(false)}
                >
                  <img src={requestedTooltip} alt="" />
                </Modal>
              </>)}
          </>
        )}
      </main>
    </div>
  );
};

export default HOP;
